import React, { useEffect, useState } from "react";
import {
  Box,
  ButtonBase,
  CircularProgress,
  Dialog,
  IconButton,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { productImageSrc } from "../../utils/productImage";

const PICTURE_MAX_HEIGHT = 320;
// Requested at 2× the displayed height so pictures stay sharp on HiDPI screens.
const PICTURE_REQUEST_HEIGHT = PICTURE_MAX_HEIGHT * 2;
const THUMB_HEIGHT = 64;

/**
 * Full-screen viewer for a product's images: the full-resolution original,
 * previous / next (buttons or arrow keys) and a thumbnail strip.
 */
export const ProductImageViewer = ({ images, name, open, initialIndex = 0, onClose, ui }) => {
  const { colors } = ui;
  const fullScreen = useMediaQuery("(max-width:600px)");
  const [index, setIndex] = useState(initialIndex);
  const [status, setStatus] = useState("loading");
  const count = images.length;
  const src = count ? productImageSrc(images[index]) : null;

  useEffect(() => {
    if (open) setIndex(initialIndex);
  }, [open, initialIndex]);
  useEffect(() => setStatus("loading"), [src]);

  const step = (delta) => setIndex((current) => (current + delta + count) % count);

  return (
    <Dialog
      open={open && count > 0}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="lg"
      fullWidth
      aria-label={`${name || "Product"} photos`}
      onKeyDown={(event) => {
        if (count < 2) return;
        if (event.key === "ArrowLeft") step(-1);
        if (event.key === "ArrowRight") step(1);
      }}
      PaperProps={{ sx: { backgroundColor: colors.bgPaper, borderRadius: fullScreen ? 0 : "12px" } }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2.5, py: 1.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            noWrap
            sx={{ fontSize: 15, fontWeight: 700, color: colors.textPrimary }}
          >
            {name || "Product"}
          </Typography>
          <Typography sx={{ fontSize: 12, color: colors.textSecondary }}>
            Photo {index + 1} of {count}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="Close photos">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: fullScreen ? "calc(100vh - 180px)" : "70vh",
          backgroundColor: ui.surfaceAlt,
        }}
      >
        {status === "loading" ? <CircularProgress size={22} sx={{ position: "absolute" }} /> : null}
        {status === "error" ? (
          <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>
            This photo could not be loaded.
          </Typography>
        ) : null}
        {src ? (
          <Box
            key={src}
            component="img"
            src={src}
            alt={`${name || "Product"} — photo ${index + 1} of ${count}`}
            onLoad={() => setStatus("loaded")}
            onError={() => setStatus("error")}
            sx={{
              display: status === "error" ? "none" : "block",
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
              opacity: status === "loaded" ? 1 : 0,
              transition: "opacity 150ms ease",
            }}
          />
        ) : null}
        {count > 1 ? (
          <>
            <IconButton
              onClick={() => step(-1)}
              aria-label="Previous photo"
              sx={{ position: "absolute", left: 12, backgroundColor: colors.bgPaper, boxShadow: 1 }}
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              onClick={() => step(1)}
              aria-label="Next photo"
              sx={{ position: "absolute", right: 12, backgroundColor: colors.bgPaper, boxShadow: 1 }}
            >
              <ChevronRightIcon />
            </IconButton>
          </>
        ) : null}
      </Box>

      {count > 1 ? (
        <Box sx={{ display: "flex", gap: 1, px: 2.5, py: 1.5, overflowX: "auto" }}>
          {images.map((image, thumbIndex) => {
            const selected = thumbIndex === index;
            return (
              <ButtonBase
                key={`${image.url || image.key}-${thumbIndex}`}
                onClick={() => setIndex(thumbIndex)}
                aria-label={`Show photo ${thumbIndex + 1}`}
                aria-current={selected ? "true" : undefined}
                sx={{
                  flex: "none",
                  borderRadius: "8px",
                  overflow: "hidden",
                  outline: `2px solid ${selected ? ui.accent : "transparent"}`,
                  outlineOffset: 2,
                  opacity: selected ? 1 : 0.7,
                  "&:hover": { opacity: 1 },
                }}
              >
                <Box
                  component="img"
                  src={productImageSrc(image, { height: THUMB_HEIGHT * 2 })}
                  alt=""
                  loading="lazy"
                  sx={{ display: "block", height: THUMB_HEIGHT, width: "auto" }}
                />
              </ButtonBase>
            );
          })}
        </Box>
      ) : null}
    </Dialog>
  );
};

/**
 * Small square cover thumbnail for headers; click to open the viewer. Shows a
 * photo count when there is more than one. Renders nothing when there is no
 * image or the cover fails to load.
 */
export const ProductThumbnail = ({ images, name, ui, size = 72 }) => {
  const list = images || [];
  const src = list.length ? productImageSrc(list[0], { height: size * 2 }) : null;
  const [failedSrc, setFailedSrc] = useState(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  if (!src || src === failedSrc) return null;

  return (
    <>
      <ButtonBase
        onClick={() => setViewerOpen(true)}
        aria-label={`View photos of ${name || "this product"}`}
        title={list.length > 1 ? `View ${list.length} photos` : "View photo"}
        sx={{
          position: "relative",
          flex: "none",
          width: size,
          height: size,
          borderRadius: "10px",
          overflow: "hidden",
          border: `1px solid ${ui.colors.border}`,
          backgroundColor: "#fff",
          cursor: "zoom-in",
        }}
      >
        <Box
          component="img"
          src={src}
          alt={name || "Product"}
          onError={() => setFailedSrc(src)}
          sx={{ display: "block", width: "100%", height: "100%", objectFit: "contain" }}
        />
        {list.length > 1 ? (
          <Box
            component="span"
            sx={{
              position: "absolute",
              right: 4,
              bottom: 4,
              px: 0.6,
              borderRadius: "6px",
              fontSize: 10.5,
              fontWeight: 700,
              lineHeight: "16px",
              color: "#fff",
              backgroundColor: "rgba(15, 23, 42, 0.65)",
            }}
          >
            {list.length}
          </Box>
        ) : null}
      </ButtonBase>
      <ProductImageViewer
        images={list}
        name={name}
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
        ui={ui}
      />
    </>
  );
};

/**
 * A product's cover image (the first of `images`); click to open the viewer.
 * Renders nothing when there is no image or the cover fails to load.
 */
const ProductPicture = ({ images, name, ui }) => {
  const list = images || [];
  const src = list.length ? productImageSrc(list[0], { height: PICTURE_REQUEST_HEIGHT }) : null;
  // Remembers which src failed, so switching products retries automatically.
  const [failedSrc, setFailedSrc] = useState(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  if (!src || src === failedSrc) return null;

  return (
    <Box sx={{ ...ui.card, mt: 2, p: 1.5, textAlign: "center" }}>
      <ButtonBase
        onClick={() => setViewerOpen(true)}
        aria-label={`View photos of ${name || "this product"}`}
        sx={{ borderRadius: "8px", cursor: "zoom-in", maxWidth: "100%" }}
      >
        <Box
          component="img"
          src={src}
          alt={name || "Product"}
          loading="lazy"
          onError={() => setFailedSrc(src)}
          sx={{
            display: "block",
            maxWidth: "100%",
            maxHeight: PICTURE_MAX_HEIGHT,
            objectFit: "contain",
            borderRadius: "8px",
          }}
        />
      </ButtonBase>
      <Typography sx={{ fontSize: 11.5, color: ui.colors.textTertiary, mt: 1 }}>
        Click to enlarge
        {list.length > 1 ? ` · ${list.length} photos` : ""}
      </Typography>
      <ProductImageViewer
        images={list}
        name={name}
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
        ui={ui}
      />
    </Box>
  );
};

export default ProductPicture;
